// Input mode: Desktop (mouse look, hold Space, mouse stroke; the original controls) or Touch (the same model on a
// touch screen, see touchControls.ts). Pure logic here (no React, no three.js), so inputMode.test.ts can check it;
// settings.ts stores the player's choice (snooker.settings.v1 inputMode).
//
//   auto (default): Touch when the device has a touch screen AND its primary pointer is coarse or can't hover
//         (a phone or tablet), Desktop otherwise. A laptop with a touch screen and a mouse/trackpad stays Desktop.
//   desktop / touch: the Settings override, whatever the device reports.
//
// The same detection decides "phone-like" for the graphics quality's first choice (graphicsQuality.ts): Auto lands
// on Low there instead of measuring the frame rate at High.

export type InputModeChoice = "auto" | "desktop" | "touch";
export type InputMode = "desktop" | "touch";

export const INPUT_MODE_CHOICES: InputModeChoice[] = ["auto", "desktop", "touch"];

export function isInputModeChoice(v: unknown): v is InputModeChoice {
    return typeof v === "string" && (INPUT_MODE_CHOICES as string[]).includes(v);
}

/** What the browser says about its pointers. */
export interface DeviceCaps {
    /** navigator.maxTouchPoints (0 without a touch screen) */
    maxTouchPoints: number;
    /** "ontouchstart" in window (older mobile browsers report maxTouchPoints 0) */
    touchEvents: boolean;
    /** matchMedia("(pointer: coarse)"): the primary pointer is a finger */
    coarsePointer: boolean;
    /** matchMedia("(hover: none)"): the primary pointer can't hover */
    noHover: boolean;
}

export const NO_TOUCH_CAPS: DeviceCaps = {maxTouchPoints: 0, touchEvents: false, coarsePointer: false, noHover: false};

type WindowLike = {
    navigator?: {maxTouchPoints?: number};
    matchMedia?: (q: string) => {matches: boolean};
} & Record<string, any>;

/** Reads the caps from a window (jsdom and old browsers without matchMedia read as "no touch"). */
export function detectCaps(win: WindowLike | undefined = typeof window !== "undefined" ? window as any : undefined): DeviceCaps {
    if (!win) return {...NO_TOUCH_CAPS};
    const mq = (q: string) => { try { return !!(win.matchMedia && win.matchMedia(q).matches); } catch { return false; } };
    const points = Number(win.navigator && win.navigator.maxTouchPoints);
    return {
        maxTouchPoints: isFinite(points) && points > 0 ? points : 0,
        touchEvents: "ontouchstart" in win,
        coarsePointer: mq("(pointer: coarse)"),
        noHover: mq("(hover: none)"),
    };
}

/** A touch-first device (phone, tablet): a touch screen whose primary pointer is coarse or has no hover. */
export function isTouchDevice(c: DeviceCaps): boolean {
    const touch = c.maxTouchPoints > 0 || c.touchEvents;
    return touch && (c.coarsePointer || c.noHover);
}

/** Phone-like for the first graphics quality choice: the same test (phones and tablets have mobile GPUs). */
export function isPhoneLike(c: DeviceCaps): boolean {
    return isTouchDevice(c);
}

/** The mode to play with: the Settings override, or for auto what the device is. */
export function resolveInputMode(choice: InputModeChoice, c: DeviceCaps): InputMode {
    if (choice === "desktop" || choice === "touch") return choice;
    return isTouchDevice(c) ? "touch" : "desktop";
}
